import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchHistoricalData } from '../services/apiService';
import { formatDisplayDateTime } from '../utils/dateTimeFormat';
import {
  computeWorkingHours,
  formatDuration,
  formatHoursDecimal,
  formatPercent
} from '../utils/workingHours';
import './ReportWorkingHoursPage.css';
import './ReportGraphsPage.css';

const DEFAULT_REPORT_START_TIME = '00:00';
const DEFAULT_REPORT_END_TIME = '23:59';

function formatTimeForApiStart(htmlTime) {
  const t = String(htmlTime || '').trim();
  if (!t) return null;
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) return t;
  if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
  return `${t}:00`;
}

function formatTimeForApiEnd(htmlTime) {
  const t = String(htmlTime || '').trim();
  if (!t) return null;
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) {
    if (t === '23:59:00') return '23:59:59';
    return t;
  }
  if (/^\d{2}:\d{2}$/.test(t)) {
    if (t === '23:59') return '23:59:59';
    return `${t}:00`;
  }
  return `${t}:00`;
}

function localDateFromYmdAndHms(yyyyMmDd, hhmmss) {
  const [y, mo, d] = String(yyyyMmDd || '')
    .split('-')
    .map((x) => parseInt(x, 10));
  if (!y || !mo || !d) return null;
  const parts = String(hhmmss || '').split(':');
  const h = parseInt(parts[0], 10) || 0;
  const mi = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2] || '0', 10) || 0;
  const dt = new Date(y, mo - 1, d, h, mi, s, 0);
  return isNaN(dt.getTime()) ? null : dt;
}

function getWindowBounds(startDate, endDate, startTime, endTime) {
  const startD = startDate || endDate;
  const endD = endDate || startDate;
  if (!startD || !endD) return null;
  const startClock = (startTime || '').trim() || DEFAULT_REPORT_START_TIME;
  const endClock = (endTime || '').trim() || DEFAULT_REPORT_END_TIME;
  const from = localDateFromYmdAndHms(startD, formatTimeForApiStart(startClock));
  const to = localDateFromYmdAndHms(endD, formatTimeForApiEnd(endClock));
  if (!from || !to) return null;
  return { from, to };
}

function formatDayLabel(yyyyMmDd) {
  const s = String(yyyyMmDd || '').trim();
  if (!s) return '';
  const d = new Date(`${s}T00:00:00`);
  if (isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const day = d.toLocaleDateString(undefined, { weekday: 'short' });
  return `${day} ${dd}-${mm}-${yyyy}`;
}

const ReportWorkingHoursPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateDates = location.state || {};

  const [startDate, setStartDate] = useState(stateDates.startDate || '');
  const [endDate, setEndDate] = useState(stateDates.endDate || '');
  const [startTime, setStartTime] = useState(() => {
    if (stateDates.startTime != null && String(stateDates.startTime).trim() !== '') return String(stateDates.startTime).trim();
    if (stateDates.startDate || stateDates.endDate) return DEFAULT_REPORT_START_TIME;
    return '';
  });
  const [endTime, setEndTime] = useState(() => {
    if (stateDates.endTime != null && String(stateDates.endTime).trim() !== '') return String(stateDates.endTime).trim();
    if (stateDates.startDate || stateDates.endDate) return DEFAULT_REPORT_END_TIME;
    return '';
  });
  const [organizationId] = useState(stateDates.organizationId || '');
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  const windowBounds = useMemo(
    () => getWindowBounds(startDate, endDate, startTime, endTime),
    [startDate, endDate, startTime, endTime]
  );

  const workingHours = useMemo(() => {
    if (!windowBounds) return null;
    return computeWorkingHours(readings, windowBounds.from, windowBounds.to);
  }, [readings, windowBounds]);

  const loadData = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const endD = endDate || startDate;
      const params = { startDate, endDate: endD, limit: 100000000 };
      const stClock = (startTime || '').trim() || DEFAULT_REPORT_START_TIME;
      const etClock = (endTime || '').trim() || DEFAULT_REPORT_END_TIME;
      const st = formatTimeForApiStart(stClock);
      const et = formatTimeForApiEnd(etClock);
      if (startDate && st) params.startTime = st;
      if (endD && et) params.endTime = et;
      if (organizationId) params.organizationId = parseInt(organizationId, 10);
      const data = await fetchHistoricalData(params);
      setReadings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load working hours data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate && (stateDates.startDate || readings.length > 0 || !error)) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime, endTime, organizationId]);

  const navigateBackState = {
    startDate,
    endDate: endDate || startDate,
    startTime: (startTime || '').trim() || DEFAULT_REPORT_START_TIME,
    endTime: (endTime || '').trim() || DEFAULT_REPORT_END_TIME
  };

  const ksebPercent =
    workingHours && workingHours.totalWindowMs > 0
      ? (workingHours.ksebMs / workingHours.totalWindowMs) * 100
      : 0;
  const inverterPercent =
    workingHours && workingHours.totalWindowMs > 0
      ? (workingHours.inverterMs / workingHours.totalWindowMs) * 100
      : 0;
  const unknownPercent =
    workingHours && workingHours.totalWindowMs > 0
      ? (workingHours.unknownMs / workingHours.totalWindowMs) * 100
      : 0;

  const activeSessions = (workingHours?.sessions || []).filter((s) => s.source);

  return (
    <div className="report-working-hours-page">
      <div className="report-working-hours-container">
        <div className="report-working-hours-header">
          <h2 className="report-working-hours-title">KSEB &amp; Inverter Working Hours</h2>
          <div className="report-working-hours-actions">
            <button
              type="button"
              onClick={() => navigate('/report', { state: navigateBackState })}
              className="btn-back-report"
            >
              ← Back to Report
            </button>
            <button type="button" onClick={() => navigate('/')} className="btn-back-dashboard">
              Dashboard
            </button>
          </div>
        </div>

        <div className="graph-filters">
          <h3 className="filter-section-title">DATE &amp; TIME</h3>
          <div className="filter-row">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  const v = e.target.value;
                  setStartDate(v);
                  if (v) {
                    if (!endDate) setEndDate(v);
                    if (!(String(startTime || '').trim())) setStartTime(DEFAULT_REPORT_START_TIME);
                    if (!(String(endTime || '').trim())) setEndTime(DEFAULT_REPORT_END_TIME);
                  } else {
                    setStartTime('');
                  }
                }}
                max={today}
                className="form-input"
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  const v = e.target.value;
                  setEndDate(v);
                  if (v) {
                    if (!startDate) setStartDate(v);
                    if (!(String(endTime || '').trim())) setEndTime(DEFAULT_REPORT_END_TIME);
                    if (!(String(startTime || '').trim())) setStartTime(DEFAULT_REPORT_START_TIME);
                  } else {
                    setEndTime('');
                  }
                }}
                max={today}
                min={startDate || undefined}
                className="form-input"
              />
            </div>
            <div className="filter-group">
              <label>Start Time</label>
              <input
                type="time"
                value={startTime}
                title="00:00 is midnight (12 AM)"
                onChange={(e) => setStartTime(e.target.value)}
                className="form-input"
                placeholder="--:--"
              />
            </div>
            <div className="filter-group">
              <label>End Time</label>
              <input
                type="time"
                value={endTime}
                title="23:59 is 11:59 PM; range ends at 23:59:59"
                onChange={(e) => setEndTime(e.target.value)}
                className="form-input"
                placeholder="--:--"
              />
            </div>
            <button
              type="button"
              onClick={loadData}
              disabled={loading || !startDate || !endDate}
              className="btn-load-working-hours"
            >
              {loading ? 'Loading...' : 'Load Data'}
            </button>
          </div>
        </div>

        {error && <div className="wh-error">{error}</div>}

        {loading && <div className="wh-loading">Calculating working hours from report data...</div>}

        {!loading && workingHours && (
          <>
            <div className="wh-meta">
              <div>
                <strong>Report window:</strong>{' '}
                {windowBounds
                  ? `${formatDisplayDateTime(windowBounds.from.toISOString())} → ${formatDisplayDateTime(windowBounds.to.toISOString())}`
                  : '—'}
              </div>
              <div>
                <strong>Samples in range:</strong> {workingHours.sampleCount}
                {workingHours.firstReading && workingHours.lastReading && (
                  <>
                    {' '}
                    | <strong>First:</strong> {formatDisplayDateTime(workingHours.firstReading.toISOString())} |{' '}
                    <strong>Last:</strong> {formatDisplayDateTime(workingHours.lastReading.toISOString())}
                  </>
                )}
              </div>
            </div>

            <div className="wh-summary-grid">
              <div className="wh-summary-card kseb">
                <p className="wh-summary-label">KSEB Working Hours</p>
                <p className="wh-summary-value">{formatDuration(workingHours.ksebMs)}</p>
                <p className="wh-summary-sub">
                  {formatHoursDecimal(workingHours.ksebMs)} hrs ({formatPercent(ksebPercent)} of window)
                </p>
              </div>
              <div className="wh-summary-card inverter">
                <p className="wh-summary-label">Inverter Working Hours</p>
                <p className="wh-summary-value">{formatDuration(workingHours.inverterMs)}</p>
                <p className="wh-summary-sub">
                  {formatHoursDecimal(workingHours.inverterMs)} hrs ({formatPercent(inverterPercent)} of window)
                </p>
              </div>
              <div className="wh-summary-card unknown">
                <p className="wh-summary-label">POWER OFF Hours</p>
                <p className="wh-summary-value">{formatDuration(workingHours.unknownMs)}</p>
                <p className="wh-summary-sub">
                  {formatHoursDecimal(workingHours.unknownMs)} hrs ({formatPercent(unknownPercent)} of window)
                </p>
              </div>
            </div>

            {workingHours.dailyBreakdown.length > 1 && (
              <>
                <h3 className="wh-section-title">Daily Breakdown</h3>
                <div className="wh-table-wrap">
                  <table className="wh-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>KSEB Hours</th>
                        <th>Inverter Hours</th>
                        <th>Other</th>
                        <th>KSEB %</th>
                        <th>Inverter %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workingHours.dailyBreakdown.map((day) => (
                        <tr key={day.date}>
                          <td>{formatDayLabel(day.date)}</td>
                          <td>{formatDuration(day.ksebMs)}</td>
                          <td>{formatDuration(day.inverterMs)}</td>
                          <td>{formatDuration(day.unknownMs)}</td>
                          <td>{formatPercent(day.ksebPercent)}</td>
                          <td>{formatPercent(day.inverterPercent)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <h3 className="wh-section-title">Load On Sessions</h3>
            {activeSessions.length === 0 ? (
              <div className="wh-empty">
                No KSEB or Inverter sessions found in the selected range. Check that report data includes Load On
                values.
              </div>
            ) : (
              <div className="wh-table-wrap">
                <table className="wh-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeSessions.map((session, idx) => (
                      <tr key={`${session.source}-${session.startMs}-${idx}`}>
                        <td>
                          <span className={`wh-badge ${session.source === 'KSEB' ? 'kseb' : 'inverter'}`}>
                            {session.source}
                          </span>
                        </td>
                        <td>{session.startLabel}</td>
                        <td>{session.endLabel}</td>
                        <td>{session.durationLabel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportWorkingHoursPage;
